/* eslint-disable react-refresh/only-export-components */
// Fast Refresh warning suppressed: Standard React context pattern exports context, provider, and hook together
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { adminApiClient } from '../lib/api';
import { SessionState, adminSession } from '../lib/session';
import { logger } from '../lib/utils/logger';
import { ADMIN_ROUTES, adminUrl } from '../config/routes';
import { useSessionMirror } from '../hooks/useSessionMirror';
import {
  useSessionTimeout,
  ADMIN_SESSION_TIMEOUT_MS,
  ADMIN_SESSION_WARNING_MS,
} from '../hooks/useSessionTimeout';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';

interface AdminUser {
  id: string;
  email: string;
  full_name?: string;
  permissions: string[];
  roles: string[];
}

interface AdminStatusResponse {
  authenticated: boolean;
  admin: AdminUser | null;
}

interface AdminLoginResponse {
  accessToken: string;
  refreshToken: string;
  // Optional in practice — keep a defensive fallback at the call site (?? 900).
  expiresIn?: number;
  admin: AdminUser;
}

interface AdminAuthContextType {
  admin: AdminUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, otp?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  // `loading` is managed explicitly (not derived from sessionState) because
  // checkAuth has two awaits — adminSession.probe() then adminApiClient.get
  // for the user object. Deriving loading from sessionState alone creates
  // a race window where state → 'alive' fires React subscribers BEFORE the
  // admin object is fetched, and the route guard sees
  // `isAuthenticated = alive && !!admin = false` → redirects to /login
  // mid-boot. Keep loading true until BOTH the probe and the admin fetch
  // complete.
  const [loading, setLoading] = useState(true);

  // Mirror adminSession's state machine into React via the shared hook. A
  // session that is Alive or Refreshing counts as active; the admin object must
  // also be loaded for the session to be authenticated (CU-868kykuzk).
  const { isSessionActive } = useSessionMirror(adminSession);
  const isAuthenticated = isSessionActive && !!admin;

  const checkAuth = useCallback(async () => {
    // Re-entrant safety: any external caller invoking checkAuth() after
    // initial mount needs the route guard to wait again while probe runs.
    // Without resetting loading=true here, the route guard would see
    // loading=false from the previous run AND the brief sessionState=probing
    // window from the new probe → isAuthenticated would flicker false and
    // redirect to /login. Defensive even though today's only caller is the
    // mount-time useEffect.
    setLoading(true);
    try {
      // probe returns the parsed /admin/auth/me body — same round-trip
      // that determines session state. No need for a second GET.
      const body = await adminSession.probe();
      if (adminSession.state === SessionState.Alive) {
        const response = body as AdminStatusResponse | null;
        if (response && response.authenticated && response.admin) {
          setAdmin(response.admin);
        } else {
          setAdmin(null);
        }
      } else {
        setAdmin(null);
      }
    } finally {
      // Always release the loading gate so the route guard can re-evaluate
      // — even when probe throws.
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string, otp?: string) => {
    try {
      // Login is a PUBLIC endpoint (no existing session); pass skipAuth: true
      // to bypass the awaitReady gate on adminApiClient.
      const response = await adminApiClient.post<AdminLoginResponse>(
        '/api/v1/admin/auth/login',
        { email, password, otp },
        { skipAuth: true },
      );
      setAdmin(response.admin);
      adminSession.loginCompleted(response.expiresIn ?? 900);
      toast.success('Admin login successful');
    } catch (error) {
      // Suppress toast for the typed MFA flows — the AdminLogin page
      // renders its own inline UX for these (OTP prompt, setup-required
      // redirect notice). Non-MFA failures still toast as before.
      const detail = (error as { details?: { detail?: { type?: string } } }).details?.detail;
      const isMfaTyped = detail?.type === 'mfa_required' || detail?.type === 'mfa_setup_required';
      if (!isMfaTyped) {
        const message = error instanceof Error ? error.message : 'Admin login failed';
        toast.error(message);
      }
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Logout via API - backend clears httpOnly cookie automatically
      await adminApiClient.post('/api/v1/admin/auth/logout');
      toast.success('Logged out successfully');
    } catch (error) {
      logger.error('Admin logout failed', error);
    } finally {
      // Always clear session state and cache, even if API call fails — matches
      // the legacy clearProactiveRefresh-on-failure behavior.
      adminSession.logoutCompleted();
      setAdmin(null);
      queryClient.clear();
    }
  }, [queryClient]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const { showWarning, secondsRemaining, dismissWarning } = useSessionTimeout(
    {
      timeoutMs: ADMIN_SESSION_TIMEOUT_MS,
      warningMs: ADMIN_SESSION_WARNING_MS,
      trackActivity: isAuthenticated, // gate on isAuthenticated, matching the user domain
    },
    async () => {
      logger.warn('Admin session idle timeout - logging out');
      await logout();
      window.location.href = adminUrl(ADMIN_ROUTES.LOGIN);
    },
  );

  const value = {
    admin,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
      {isAuthenticated && (
        <SessionTimeoutWarning
          isOpen={showWarning}
          secondsRemaining={secondsRemaining}
          onStayLoggedIn={dismissWarning}
          onLogout={async () => {
            await logout();
            window.location.href = adminUrl(ADMIN_ROUTES.LOGIN);
          }}
        />
      )}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = React.useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
